
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Button } from '@/components/ui/button';

import { Settings, LogOut, AlertTriangle, Shield, User } from 'lucide-react';
import { SportsIQsLogo } from '@/components/SportsIQsLogo';

export const Navigation = () => {
  const { profile, signOut, isImpersonating, originalAdminProfile, stopImpersonation, effectiveRole } = useAuth();
  const { data: permissions } = useUserPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleStopImpersonation = async () => {
    const { error } = await stopImpersonation();
    if (!error) {
      navigate('/admin');
    }
  };

  if (!profile) return null;

  // Check if current user is a superadmin (not impersonating)
  const isSuperAdmin = effectiveRole === 'superadmin' && !isImpersonating;
  const isHomepage = location.pathname === '/';

  return (
    <div>
      {/* Impersonation Banner */}
      {isImpersonating && originalAdminProfile && (
        <div className="bg-orange-100 border-b-2 border-orange-300 px-4 py-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div className="text-orange-800">
                <span className="font-semibold">Impersonating:</span>{' '}
                {profile.first_name} {profile.last_name} ({profile.role})
                <span className="ml-4 text-sm">
                  Admin: {originalAdminProfile.first_name} {originalAdminProfile.last_name}
                </span>
              </div>
            </div>
            <Button 
              onClick={handleStopImpersonation}
              variant="destructive" 
              size="sm"
              className="bg-orange-600 hover:bg-orange-700"
            >
              Stop Impersonation
            </Button>
          </div>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center space-x-2 text-lg font-semibold">
                <SportsIQsLogo />
                <span>Sports IQs</span>
              </Link>
              
              
              {!isImpersonating && effectiveRole === 'superadmin' && (
                  <Link to="/superadmin">
                    <Button variant="ghost" className="flex items-center space-x-2 text-red-600 hover:text-red-700">
                      <Shield className="h-4 w-4" />
                      <span>Admin</span>
                    </Button>
                  </Link>
              )}
              
              {/* Administration - Only show for users with admin permissions */}
              {!isImpersonating && !isSuperAdmin && permissions?.hasAdminAccess && (
                <Link to="/admin">
                  <Button variant="ghost" className="flex items-center space-x-2">
                    <Settings className="h-4 w-4" />
                    <span>Administration</span>
                  </Button>
                </Link>
              )}
            </div>

            <div className="flex items-center space-x-4">
              
              <span className="text-sm text-muted-foreground flex items-center">
                {effectiveRole === 'superadmin' ? (
                  <Shield className="h-4 w-4 mr-2 text-red-600" />
                ) : (
                  <User className="h-4 w-4 mr-2" />
                )}
                {profile.first_name} {profile.last_name}
              </span>
              <Button variant="ghost" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};
